// ============================================================
//  Per-category JSON loader.
//
//  Reads templates + calculator-bundle JSON files for a given
//  trade/category combination directly from /documents/categories/.
//  Vite's `import.meta.glob` resolves the JSON files at build time
//  so they ship with the bundle — no fetch round-trip at runtime.
//
//  v2 folder layout (per-category):
//    templates/  NN-<slug>-templates.json     (EstimateTemplate[] — pre-priced bundles)
//    calculators/02-templates.json            (calculator-template ServiceTemplate[] — formulas)
//    calculators/05-calculator-bundle.json    (CalculatorBundle — UI meta + sanity rules + pricebook seed)
// ============================================================

import type { CategoryBundle, ServiceTemplate, CalculatorBundle } from './templateTypes';
import type { EstimateTemplate } from './estimateTemplateTypes';

// `eager: true` inlines the JSON at build time.
const ESTIMATE_TEMPLATE_FILES = import.meta.glob('/documents/categories/*/*/templates/*.json', { eager: true, import: 'default' }) as Record<string, EstimateTemplate[]>;
const CALC_TEMPLATE_FILES = import.meta.glob('/documents/categories/*/*/calculators/02-templates.json', { eager: true, import: 'default' }) as Record<string, ServiceTemplate[]>;
const CALC_BUNDLE_FILES = import.meta.glob('/documents/categories/*/*/calculators/05-calculator-bundle.json', { eager: true, import: 'default' }) as Record<string, CalculatorBundle>;

/** Path → "trade/categorySlug" (the folder slug, kebab-cased like `01-design-and-consultation`). */
function parsePath(p: string): { trade: string; categorySlug: string } | null {
  const m = p.match(/\/categories\/([^/]+)\/([^/]+)\//);
  return m ? { trade: m[1], categorySlug: m[2] } : null;
}

/** Map a UI category id (e.g. `design`) to the on-disk folder slug for a trade. */
const CATEGORY_FOLDER_BY_ID: Record<string, Record<string, string>> = {
  landscaping: {
    'design': '01-design-and-consultation',
    'site-prep': '02-site-prep-and-demolition',
    'tree-care': '03-tree-care',
    'planting': '04-planting-and-softscape',
    'lawn-care': '05-lawn-care-turf',
    'mulch-beds': '06-mulch-and-beds',
    'irrigation': '07-irrigation-water-management',
    'hardscape': '08-hardscape',
    'concrete': '09-concrete',
    'drainage': '10-drainage-and-grading',
    'structures': '11-outdoor-structures',
    'lighting': '12-outdoor-lighting',
    'water-fire': '13-water-and-fire-features',
    'snow-ice': '14-snow-and-ice-management',
    'seasonal': '15-seasonal-and-recurring-bundles',
    'pest-control': '16-plant-health-care-and-pest-control',
    'specialty': '17-specialty-niche',
  },
  roofing: {
    'inspection':            '01-inspection-and-assessment',
    'asphalt-shingle':       '02-asphalt-shingle-roofing',
    'metal-roofing':         '03-metal-roofing',
    'tile-roofing':          '04-tile-roofing',
    'low-slope-membrane':    '05-low-slope-membrane',
    'wood-shake-slate':      '06-wood-shake-and-slate',
    'decking-underlayment':  '07-decking-and-underlayment',
    'repair-leak-stop':      '08-roof-repair-and-leak-stop',
    'flashing-penetrations': '09-flashing-and-penetrations',
    'gutters':               '10-gutters-and-downspouts',
    'ventilation':           '11-roof-ventilation',
    'skylights':             '12-skylights-and-sun-tunnels',
    'storm-insurance':       '13-storm-emergency-and-insurance',
    'maintenance-cleaning':  '14-maintenance-and-cleaning',
    'solar-specialty':       '15-solar-and-specialty',
  },
  electrical: {
    'inspection':         '01-inspection-and-diagnostics',
    'panel-service':      '02-service-and-panel-upgrades',
    'wiring':             '03-wiring-and-rewiring',
    'devices':            '04-outlets-switches-and-devices',
    'interior-lighting':  '05-interior-lighting',
    'exterior-lighting':  '06-exterior-and-landscape-lighting',
    'fans-ventilation':   '07-ceiling-fans-and-ventilation',
    'ev-charging':        '08-ev-charger-installation',
    'generators':         '09-generators-and-backup-power',
    'dedicated-circuits': '10-appliance-and-equipment-circuits',
    'smart-low-voltage':  '11-smart-home-and-low-voltage',
    'surge-grounding':    '12-surge-protection-and-grounding',
    'safety-code':        '13-safety-and-code-corrections',
    'commercial':         '14-commercial-and-specialty',
    'solar-storage':      '15-solar-interconnection-and-storage',
  },
  plumbing: {
    'inspection':           '01-inspection-and-diagnostics',
    'water-heaters':        '02-water-heaters',
    'repiping':             '03-repiping-and-water-supply',
    'drain-sewer':          '04-drain-cleaning-and-sewer',
    'fixtures':             '05-fixtures-and-faucets',
    'gas-lines':            '06-gas-lines',
    'water-treatment':      '07-water-treatment-and-filtration',
    'sump-drainage':        '08-sump-pumps-and-drainage',
    'repair-leak-stop':     '09-repair-and-leak-stop',
    'rough-in':             '10-rough-in-and-remodel',
    'backflow':             '11-backflow-and-cross-connection',
    'well-systems':         '12-well-and-pump-systems',
    'maintenance':          '13-maintenance-and-service-plans',
    'commercial-specialty': '14-commercial-and-specialty',
    'outdoor-site':         '15-outdoor-and-site-plumbing',
  },
  hvac: {
    'inspection':           '01-inspection-and-diagnostics',
    'central-ac':           '02-central-air-conditioning',
    'heat-pumps':           '03-heat-pumps-and-geothermal',
    'furnaces':             '04-furnaces-and-forced-air-heating',
    'boilers-hydronic':     '05-boilers-and-hydronic-heating',
    'mini-split':           '06-ductless-mini-split-systems',
    'ductwork':             '07-ductwork-and-air-distribution',
    'ventilation':          '08-ventilation-and-air-exchange',
    'indoor-air-quality':   '09-indoor-air-quality',
    'thermostats-controls': '10-thermostats-zoning-and-controls',
    'maintenance':          '11-maintenance-and-tune-up-plans',
    'repair':               '12-repair-and-emergency-service',
    'refrigerant':          '13-refrigerant-services',
    'commercial':           '14-commercial-and-rooftop-systems',
    'water-heating':        '15-water-heating',
    'specialty':            '16-specialty-and-niche-systems',
  },
  painting: {
    'consultation':        '01-consultation-and-project-prep',
    'interior':            '02-interior-painting',
    'exterior':            '03-exterior-painting',
    'cabinets':            '04-cabinet-and-builtin-refinishing',
    'wood-staining':       '05-wood-staining-and-finishing',
    'drywall-prep':        '06-drywall-repair-and-surface-prep',
    'decorative':          '07-specialty-and-decorative-finishes',
    'floor-coatings':      '08-epoxy-and-concrete-floor-coatings',
    'industrial':          '09-industrial-and-protective-coatings',
    'wall-coverings':      '10-wall-coverings',
    'pressure-washing':    '11-pressure-washing-and-cleaning',
    'commercial':          '12-commercial-and-multi-unit',
    'trim-doors':          '13-trim-doors-and-detail',
    'maintenance':         '14-maintenance-and-touch-up',
    'surface-remediation': '15-surface-prep-and-remediation',
  },
  concrete: {
    'consultation':          '01-consultation-and-site-assessment',
    'driveways':             '02-driveways',
    'patios-walkways':       '03-patios-and-walkways',
    'slabs-flatwork':        '04-slabs-and-flatwork',
    'foundations':           '05-foundations-and-footings',
    'stamped-decorative':    '06-stamped-and-decorative',
    'staining-polishing':    '07-staining-and-polishing',
    'overlays-resurfacing':  '08-overlays-and-resurfacing',
    'repair':                '09-concrete-repair',
    'slab-leveling':         '10-slab-leveling-and-lifting',
    'retaining-walls':       '11-retaining-walls-and-structural',
    'steps-stairs':          '12-steps-stairs-and-stoops',
    'curbs-edging':          '13-curbs-edging-and-ada',
    'demolition':            '14-demolition-and-removal',
    'sealing-maintenance':   '15-sealing-and-maintenance',
  },
  cleaning: {
    'recurring-residential':  '01-recurring-residential-cleaning',
    'deep-cleaning':          '02-deep-and-one-time-cleaning',
    'move-in-out':            '03-move-in-move-out-cleaning',
    'str-turnover':           '04-vacation-rental-turnover',
    'commercial-janitorial':  '05-commercial-janitorial-office',
    'medical-healthcare':     '06-medical-and-healthcare-cleaning',
    'post-construction':      '07-post-construction-cleaning',
    'carpet-upholstery':      '08-carpet-and-upholstery-cleaning',
    'hard-floor-care':        '09-hard-floor-care-and-refinishing',
    'pressure-washing':       '10-pressure-and-power-washing',
    'window-cleaning':        '11-window-and-glass-cleaning',
    'gutter-cleaning':        '12-gutter-cleaning',
    'disinfection':           '13-disinfection-and-sanitization',
    'air-duct':               '14-air-duct-and-vent-cleaning',
    'specialty-restoration':  '15-specialty-and-restoration-cleaning',
  },
};

/** Synchronously load the calculator bundle (formulas + UI meta + sanity rules)
 *  for a trade/category. Returns null when the category has no calculators/
 *  subfolder yet. */
export function loadCategoryBundle(trade: string, categoryId: string): CategoryBundle | null {
  const folder = CATEGORY_FOLDER_BY_ID[trade]?.[categoryId];
  if (!folder) return null;

  const templatesEntry = Object.entries(CALC_TEMPLATE_FILES).find(([p]) => {
    const parsed = parsePath(p);
    return parsed?.trade === trade && parsed.categorySlug === folder;
  });
  const bundleEntry = Object.entries(CALC_BUNDLE_FILES).find(([p]) => {
    const parsed = parsePath(p);
    return parsed?.trade === trade && parsed.categorySlug === folder;
  });

  if (!templatesEntry || !bundleEntry) return null;

  return {
    trade,
    category: categoryId,
    templates: templatesEntry[1],
    bundle: bundleEntry[1],
  };
}

/** Synchronously load the v2 estimate templates (pre-priced bundles) for a
 *  trade/category. Returns null when the category has no templates/ subfolder
 *  populated yet. */
export function loadEstimateTemplates(trade: string, categoryId: string): EstimateTemplate[] | null {
  const folder = CATEGORY_FOLDER_BY_ID[trade]?.[categoryId];
  if (!folder) return null;

  const entry = Object.entries(ESTIMATE_TEMPLATE_FILES).find(([p]) => {
    const parsed = parsePath(p);
    return parsed?.trade === trade && parsed.categorySlug === folder;
  });

  return entry ? entry[1] : null;
}

export function hasCategoryBundle(trade: string, categoryId: string): boolean {
  return loadCategoryBundle(trade, categoryId) !== null;
}

export function hasEstimateTemplates(trade: string, categoryId: string): boolean {
  return loadEstimateTemplates(trade, categoryId) !== null;
}

/** Look up a bundled estimate template by its `id` across all trades and
 *  categories. Used by the Save flow to diff a zone's current items against
 *  the original template the contractor started from. */
export function findEstimateTemplateById(id: string): EstimateTemplate | null {
  for (const arr of Object.values(ESTIMATE_TEMPLATE_FILES)) {
    const found = arr.find(t => t.id === id);
    if (found) return found;
  }
  return null;
}
